import type { PartialDeep, UndefinedOnPartialDeep } from 'type-fest';

import type {
    ArticleMeasurement,
    AssetArticle,
    AssetDataType,
    CustomerType,
    MeasurementSystem,
    PackageMeasurement,
} from '@repo/types/asset';
import { type CartData as DraftCartData, type Item as DraftItem, type SalesLocation, isDraft } from '@repo/types/draft';
import { type OrderAsset } from '@repo/types/order';
import { type CartData as QuotationCartData, type Item as QuotationItem, isQuotation } from '@repo/types/quotation';
import { filterNonNullProperties, isDefined, isNumber } from '@repo/utils/type.utils';

import { getAssetData, selectAssetDetails, selectCurrencyCode } from './customer-assets.selectors';
import { extractCustomerViewType } from './customer.selectors.ts';
import { findEntityMatchingViewType, getCustomerViewType } from './utils';

export type CartData = OrderAsset | PartialDeep<DraftCartData> | PartialDeep<QuotationCartData>;

type AssetArticleItem = UndefinedOnPartialDeep<Partial<DraftItem | QuotationItem>>;
type AssetArticleSalesLocation = NonNullable<
    NonNullable<NonNullable<AssetArticleItem['retailItem']>['storeInfo']>['salesLocations']
>[number];

const LITRE_UNIT = 'litre';
const CUBIC_METRE_UNIT = 'm³';
const LITRES_PER_CUBIC_METRE = 1000;

const DEFAULT_MEASUREMENT_SYSTEM: MeasurementSystem = 'metric';

interface PackageMeasurementSource {
    packageMeasurements?: Array<UndefinedOnPartialDeep<PartialDeep<PackageMeasurement>>> | undefined;
}

export interface AssetArticleOptions {
    /** Which measurement system package weights and volumes are resolved to. Defaults to metric. */
    measurementSystem?: MeasurementSystem | undefined;
}

/**
 * Sums the package measurements of a single article for one measurement type, in the
 * requested system. An article spanning several packages has one entry per package.
 *
 * Returns `undefined` when the item has no entry of that type, when any entry is
 * missing a value for the requested system, or when entries disagree on the unit —
 * a partial sum would silently understate the total.
 *
 * Metric volume is reported in litres by the API and converted to cubic metres; every
 * other unit is passed through as the API reports it.
 */
const selectPackageMeasurement = (
    retailItem: PackageMeasurementSource | undefined,
    type: 'WEIGHT' | 'VOLUME',
    system: MeasurementSystem
): ArticleMeasurement | undefined => {
    const entries = retailItem?.packageMeasurements?.filter((measurement) => measurement.type === type);
    const values = entries?.map((entry) => (system === 'metric' ? entry.metric : entry.imperial));
    const unit = values?.[0]?.unit;

    if (!unit || !values?.every((value) => isNumber(value?.value) && value?.unit === unit)) {
        return;
    }

    const total = values.reduce((sum, value) => sum + (value?.value ?? 0), 0);

    return unit === LITRE_UNIT
        ? { value: total / LITRES_PER_CUBIC_METRE, unit: CUBIC_METRE_UNIT }
        : { value: total, unit };
};

export const selectCartData = getAssetData(
    (data) => data,
    (data) => data.assetDetails.cartData,
    (data) => data.assetDetails.cartData
);

export const selectAssetSummaries = (data?: AssetDataType) => {
    if (isDraft(data) || isQuotation(data)) {
        return data.assetDetails.cartData?.salesPriceData?.orderSummary?.summary;
    }

    return;
};

export const selectAssetArticles = (data?: AssetDataType, options?: AssetArticleOptions) => {
    const assetDetails = selectAssetDetails(data);
    if (!assetDetails) {
        return [];
    }

    const cartData = selectCartData(data);
    if (!cartData) {
        return [];
    }

    const customerViewType = extractCustomerViewType(assetDetails);
    const currencyCode = selectCurrencyCode(data);
    const measurementSystem = options?.measurementSystem ?? DEFAULT_MEASUREMENT_SYSTEM;

    return normalizeAssetArticleData(cartData, { customerViewType, currencyCode, measurementSystem });
};

export const selectTitleDetail = (data?: AssetDataType) => {
    const cartData = selectCartData(data);

    if (!cartData) {
        return;
    }

    return normalizeTitleDetailData(cartData);
};

export const selectAssetSummaryArticles = (data?: AssetDataType, options?: AssetArticleOptions) => {
    const articles = selectAssetArticles(data, options);
    return filterAssetSummaryArticles(articles).sort(sortBySequenceNumber);
};

const sortBySequenceNumber = (a: AssetArticle, b: AssetArticle): number => (a.seqNo ?? 0) - (b.seqNo ?? 0);

const filterAssetSummaryArticles = (items: AssetArticle[]): AssetArticle[] => {
    // only article items are applicable for asset summary
    const isArticleApplicableForSummary = (item: AssetArticle) => {
        // text lines are not applicable to summary view
        if (item.type === 'TEXTLINE') {
            return false;
        }

        return Boolean(item.itemId);
    };

    return items.flatMap((item) => {
        if (['GROUP', 'PRODUCT'].includes(item.type ?? '')) {
            return filterAssetSummaryArticles(item.childItems ?? []);
        }

        return isArticleApplicableForSummary(item) ? [item] : [];
    });
};

const normalizeAssetArticleData = (
    data: CartData,
    options: {
        customerViewType: CustomerType;
        currencyCode?: string | undefined;
        measurementSystem: MeasurementSystem;
    }
) => {
    if ('assetDetails' in data) {
        return getOrderItems(data, options.measurementSystem);
    }

    return data.items?.map((item) => getAssetArticleItem(item, options)).map(filterNonNullProperties) ?? [];
};

const normalizeTitleDetailData = (data: CartData) => {
    if ('assetDetails' in data) {
        return getOrderTitleDetail(data);
    }

    const viewType = getCustomerViewType(data.customer);
    const price = data.salesPriceData?.orderSubtotal?.find((item) => item.viewType === viewType);

    return {
        groupCount: data.orderItems?.filter((item) => item.itemType === 'GROUP').length,
        itemCount: data.totalItemCount,
        packageCount: data.totalPackages ?? 0,
        weight: data.totalWeight,
        unit: data.weightUnit,
        totalPrice: price?.product?.exclSavings.priceInclTax ?? 0,
        currency: data.total?.Currency,
    };
};

const getOrderTitleDetail = (data?: OrderAsset) => {
    const retailItems = data?.assetDetails.retailItems;
    const articles = data?.assetDetails._embedded?.Articles;
    const groups = data?.assetDetails._embedded?.Groups;
    const mappedArticles = articles?.map((item) => ({
        quantity: item.quantity,
        ...retailItems?.find((i) => i.itemId === item.itemId),
    }));

    return {
        groupCount: groups?.length ?? 0,
        itemCount: mappedArticles?.map((item) => item.quantity ?? 1).reduce((a, b) => a + b, 0) ?? 0,
        packageCount:
            mappedArticles
                ?.map((item) => (item.numberOfPackages ?? 1) * (item.quantity ?? 1))
                .reduce((a, b) => a + b, 0) ?? 0,
        weight:
            mappedArticles
                ?.map((item) => (item.packageWeight ?? 0) * (item.quantity ?? 1))
                .reduce((a, b) => a + b, 0) ?? 0,
        unit: 'kg', // TODO: get from API
        totalPrice: data?.assetDetails.Amounts?.GoodsValue?.grossAmount ?? 0,
        currency: data?.assetDetails.orderCurrencyCode ?? '',
    };
};

const getItemType = (item: UndefinedOnPartialDeep<Partial<DraftItem | QuotationItem>>) => {
    if (item.itemType === 'TEXTLINE') {
        return 'TEXTLINE';
    }
    if (item.isGroup) {
        return 'GROUP';
    }
    if (item.childItems && item.childItems.length > 0) {
        return 'PRODUCT';
    }
    return 'ARTICLE';
};

const normalizeDescription = (description?: string): string | undefined => {
    const normalizedDescription = description?.replace(/\s+/g, ' ').replace(/,\s*$/, '').trim();

    return normalizedDescription === '' ? undefined : normalizedDescription;
};

const isDraftSalesLocation = (
    salesLocation: AssetArticleSalesLocation
): salesLocation is AssetArticleSalesLocation & UndefinedOnPartialDeep<Partial<SalesLocation>> =>
    'SatelliteSalesLocation' in salesLocation;

const getAssetArticleItem = (
    item: AssetArticleItem,
    options: { customerViewType: CustomerType; currencyCode?: string | undefined; measurementSystem: MeasurementSystem }
): AssetArticle => {
    const { customerViewType, currencyCode, measurementSystem } = options;
    const primarySalesLocation = item.retailItem?.storeInfo?.salesLocations?.find(
        (salesLocation) => salesLocation.IsPrimary
    );

    const totalPrice = (
        findEntityMatchingViewType(item.subtotals?.prices, [customerViewType]) ?? item.subtotals?.prices[0]
    )?.inclSavings;

    const unitPrice = item.isGroup ? totalPrice : item.price;
    const isSelfServe =
        item.retailItem?.storeInfo?.isSelfServe ?? Boolean(primarySalesLocation?.SelfServeSalesLocations);

    // also use root currency, as item.totalPrice?.currencyCode might not always be available.
    const currency = item.totalPrice?.currencyCode?.trim() ? item.totalPrice.currencyCode : currencyCode;

    return {
        type: getItemType(item),
        name: item.name,
        itemId: item.itemId,
        referenceId: item.id,
        isReferenceItem: item.isReferenceItem ?? false,
        seqNo: item.seqNo,
        groupNo: item.isGroup ? item.id : undefined,
        imageUrl: item.retailItem?.imageUrl ?? '',
        description: normalizeDescription(item.retailItem?.itemDescription),
        quantity: item.quantity,
        isSelfServe,
        unitPrice: unitPrice?.priceInclTax,
        totalPrice: totalPrice?.priceInclTax,
        unitPriceIncludingTax: unitPrice?.priceInclTax,
        unitPriceExcludingTax: unitPrice?.priceExclTax,
        totalPriceIncludingTax: totalPrice?.priceInclTax,
        totalPriceExcludingTax: totalPrice?.priceExclTax,
        numberOfPackages: item.retailItem?.numberOfPackages,
        packageWeight: selectPackageMeasurement(item.retailItem, 'WEIGHT', measurementSystem),
        packageVolume: selectPackageMeasurement(item.retailItem, 'VOLUME', measurementSystem),
        selfServeSalesLocations: primarySalesLocation?.SelfServeSalesLocations,
        serveSalesLocations:
            primarySalesLocation && isDraftSalesLocation(primarySalesLocation)
                ? primarySalesLocation.SatelliteSalesLocation
                : undefined,
        currency,
        childItems: item.childItems?.map((childItem) => getAssetArticleItem(childItem, options)),
    };
};

const getOrderItems = (data: OrderAsset, measurementSystem: MeasurementSystem) => {
    if (!data.assetDetails._embedded) {
        return [];
    }
    const retailItems = data.assetDetails.retailItems;
    const articles: AssetArticle[] =
        data.assetDetails._embedded.Articles?.map((item) => {
            const retailItem = retailItems?.find((i) => i.itemId === item.itemId);
            const quantity = item.quantity ?? 1;
            const unitPriceIncludingTax = item.unitPrice?.salesPrice.priceInclTax ?? item.salesPrice?.value;
            const unitPriceExcludingTax = item.unitPrice?.salesPrice.priceExclTax;
            const totalPriceIncludingTax =
                item.itemPriceDetails?.inclSavings?.priceInclTax ??
                (unitPriceIncludingTax === undefined ? undefined : unitPriceIncludingTax * quantity);
            const totalPriceExcludingTax =
                item.itemPriceDetails?.inclSavings?.priceExclTax ??
                (unitPriceExcludingTax === undefined ? undefined : unitPriceExcludingTax * quantity);

            return {
                type: 'ARTICLE',
                itemId: item.itemId,
                seqNo: item.seqNo,
                // referenceId is not part of the API response, this property is created and used to filter articles that are part of a group
                // the group `referencedItems` property is an array containing `soSalesItemNo` values
                // I can't find any information about `externalRefId` property but it looks like it is the same value as `soSalesItemNo` but instead of a `number`, it is a `string`
                // TODO: check if `externalRefId` is the same as `soSalesItemNo` and remove the `?? Number(item.externalRefId)` part
                // TODO: check if soSalesItemNo is always part of the response, otherwise add a fallback
                referenceId: item.soSalesItemNo ?? Number(item.externalRefId),
                name: retailItem?.name ?? item.name,
                description: normalizeDescription(retailItem?.itemDescription) ?? '',
                imageUrl: retailItem?.imageUrl ?? '',
                isReferenceItem: item.isReferenceItem ?? false,
                isSelfServe: item.isPickedByCustomer,
                quantity: item.quantity,
                unitPrice: item.salesPrice?.value,
                ...(item.salesPrice?.value && item.quantity && { totalPrice: item.salesPrice.value * item.quantity }),
                unitPriceIncludingTax,
                unitPriceExcludingTax,
                totalPriceIncludingTax,
                totalPriceExcludingTax,
                numberOfPackages: retailItem?.numberOfPackages,
                packageWeight: selectPackageMeasurement(retailItem, 'WEIGHT', measurementSystem),
                packageVolume: selectPackageMeasurement(retailItem, 'VOLUME', measurementSystem),
                currency: data.assetDetails.orderCurrencyCode,
                savings: item.itemPriceDetails?.savings ?? [],
            };
        }) ?? [];
    const products: AssetArticle[] =
        data.assetDetails._embedded.Products?.map((product) => {
            const retailItem = retailItems?.find((item) => item.itemId === product.itemId);
            const items = articles
                .filter((article) => article.referenceId && product.referencedItems?.includes(article.referenceId))
                .sort(sortBySequenceNumber);
            return {
                type: 'PRODUCT',
                itemId: product.itemId,
                seqNo: product.seqNo,
                referenceId: product.soSalesItemNo,
                name: retailItem?.name ?? product.name,
                description: normalizeDescription(retailItem?.itemDescription),
                imageUrl: retailItem?.imageUrl ?? '',
                quantity: product.quantity,
                ...(product.salesPrice?.value && { unitPrice: product.salesPrice.value }),
                ...(product.salesPrice?.value &&
                    product.quantity && { totalPrice: product.salesPrice.value * product.quantity }),
                currency: data.assetDetails.orderCurrencyCode,
                childItems: items,
            };
        }) ?? [];

    const textLines: AssetArticle[] =
        data.assetDetails._embedded.TextLines?.map((textLine) => ({
            type: 'TEXTLINE',
            name: textLine.name,
            seqNo: textLine.seqNo,
            referenceId: textLine.groupNoReference,
            itemId: `${textLine.groupNoReference} - ${textLine.seqNo}`,
        })) ?? [];

    const groups: AssetArticle[] =
        data.assetDetails._embedded.Groups?.map((group) => {
            const items = articles
                .concat(products)
                .concat(textLines)
                .filter((item) => {
                    // find articles and products
                    if (item.referenceId && group.referencedItems?.includes(item.referenceId)) {
                        return true;
                    }
                    // find textlines
                    return group.groupNo === item.referenceId;
                })
                .sort(sortBySequenceNumber);
            const itemsExclReferenceArticles = items.filter((item) => !item.isReferenceItem);
            const totalPrice = itemsExclReferenceArticles.reduce((acc, item) => acc + (item.totalPrice ?? 0), 0);
            return {
                type: 'GROUP',
                name: group.groupName,
                seqNo: group.seqNo,
                referenceId: group.seqNo,
                groupNo: group.groupNo,
                totalPrice,
                currency: data.assetDetails.orderCurrencyCode,
                childItems: items,
            };
        }) ?? [];

    // group contains article, product and textline ref
    const groupRefIds = groups
        .map((i) => i.childItems?.map((el) => el.referenceId))
        .flat(2)
        .filter(isDefined);

    const allArticleRefIds = articles.map((i) => i.referenceId);
    const allProductRefIds = products.map((i) => i.referenceId);
    const allGroupNos = groups.map((i) => i.groupNo);

    const groupArticleRefIds = groupRefIds.reduce<number[]>((memo, id) => {
        // if id is article ref
        if (allArticleRefIds.includes(id)) {
            memo.push(id);
        }

        // if id is product ref
        if (allProductRefIds.includes(id)) {
            const product = products.find((i) => i.referenceId === id);
            memo.push(...(product?.childItems?.map((el) => el.referenceId ?? 0) ?? []));
        }

        return memo;
    }, []);

    // get products that are not in the group
    const productsInList = products.filter(
        (product) => product.referenceId && !groupRefIds.includes(product.referenceId)
    );

    // get textlines that are not in the group
    const textlinesInList = textLines.filter((textline) => !allGroupNos.includes(textline.referenceId));

    // get articles that are not in the group or product
    const articlesInList = articles.filter(
        (article) =>
            article.referenceId &&
            !groupArticleRefIds.includes(article.referenceId) &&
            productsInList.every(
                (product) => !product.childItems?.map((el) => el.referenceId).includes(article.referenceId)
            )
    );

    const items = [...groups, ...productsInList, ...articlesInList, ...textlinesInList].sort(sortBySequenceNumber);

    return items;
};
